/**
 * Canonical API error contract for the nextsub frontend.
 *
 * Wire shape (flat, per the unified error contract):
 *
 *   {
 *     "code":       "<ApiErrorCode>",
 *     "message":    "<human readable>",
 *     "details":    <any structured detail> | omitted,
 *     "request_id": "<correlation id>"     | omitted
 *   }
 *
 * The set of backend-emitted `code` values is FROZEN; see the comment on
 * `ApiErrorCode` below. The frontend owns one synthetic local code,
 * `network_error`, produced only when no HTTP response was received.
 *
 * Callers should construct `ApiError` exclusively through `toApiError` so
 * that request IDs, fallback copy, and code/status mapping stay centralised.
 */

const CANONICAL_BACKEND_CODES = [
  "validation_failed",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "idempotency_conflict",
  "quota_exceeded",
  "rate_limited",
  "readiness_check_failed",
  "internal_error",
] as const;

export type CanonicalBackendErrorCode = (typeof CANONICAL_BACKEND_CODES)[number];

/** Canonical, closed union of error codes. `network_error` is frontend-local. */
export type ApiErrorCode = CanonicalBackendErrorCode | "network_error";

/** The flat error envelope returned by the nextsub API. */
export interface ApiErrorEnvelope {
  code: ApiErrorCode;
  message?: string;
  details?: unknown;
  /** Backend correlation id; body wins over the `X-Request-ID` header. */
  request_id?: string;
}

/** Normalised error thrown by the API client and surfaced to TanStack Query. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: unknown;
  /**
   * Request correlation id. Preferred source is the response body's
   * `request_id`; falls back to the `X-Request-ID` response header. Show
   * this in support/error UI so operators can correlate with server logs.
   */
  readonly requestId?: string;

  constructor(args: {
    code: ApiErrorCode;
    message: string;
    status: number;
    details?: unknown;
    requestId?: string;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
    this.requestId = args.requestId;
  }

  get isAuth(): boolean {
    return this.code === "unauthorized" || this.status === 401;
  }

  get isConflict(): boolean {
    return this.code === "conflict" || this.code === "idempotency_conflict" || this.status === 409;
  }
}

/**
 * User-friendly fallback copy for every canonical code. The backend's
 * `message` is preferred when present; these strings only run when the
 * server omitted one or the response could not be parsed.
 */
const FALLBACK_MESSAGES: Record<ApiErrorCode, string> = {
  validation_failed: "The submitted data is invalid.",
  not_found: "The requested resource was not found.",
  conflict: "The resource was modified by someone else. Reload and retry.",
  unauthorized: "Your session has expired. Please sign in again.",
  forbidden: "You do not have permission to do that.",
  idempotency_conflict: "This action conflicts with a previous request.",
  quota_exceeded: "You have reached the plan quota. Upgrade to continue.",
  rate_limited: "Too many requests. Please slow down.",
  readiness_check_failed: "The service is not ready. Please try again in a moment.",
  internal_error: "Something went wrong on our end. Try again shortly.",
  network_error: "Could not reach the server. Check your connection.",
};

/** The union of backend codes that may appear in a server response. */
const BACKEND_CODE_SET: ReadonlySet<string> = new Set(CANONICAL_BACKEND_CODES);

/** Narrow an unknown value to `ApiErrorEnvelope` iff it is a flat envelope. */
function readEnvelope(body: unknown): ApiErrorEnvelope | undefined {
  if (!body || typeof body !== "object") return undefined;
  const candidate = body as Record<string, unknown>;
  // Reject the legacy nested `{error:{...}}` shape outright: a top-level
  // `code` string is required for a valid envelope.
  if (typeof candidate.code !== "string") return undefined;
  // Only accept canonical codes (or the frontend-local network_error,
  // which the server will never emit but the type allows by symmetry).
  if (candidate.code !== "network_error" && !BACKEND_CODE_SET.has(candidate.code)) {
    return undefined;
  }
  if (typeof candidate.message !== "string" && candidate.message !== undefined) {
    return undefined;
  }
  // A non-string request_id is dropped (header wins), but the rest of
  // the envelope stays valid; do not reject the whole body for it.
  return candidate as unknown as ApiErrorEnvelope;
}

/** Map an HTTP status to a canonical code when no envelope was parsed. */
function statusToCode(status: number): ApiErrorCode {
  if (status === 0) return "network_error";
  if (status === 400 || status === 422) return "validation_failed";
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 429) return "rate_limited";
  if (status === 503) return "readiness_check_failed";
  return "internal_error";
}

/**
 * Build an `ApiError` from a parsed error body + response.
 *
 * Resolution order:
 *
 *   1. Parse the body as the canonical flat envelope. Nested envelopes
 *      (`{error:{...}}`) and malformed bodies are rejected and we fall
 *      through to status-based mapping.
 *   2. Use the envelope's `code`, else infer from the HTTP status.
 *   3. Prefer the body's `message`; otherwise pick the canonical
 *      fallback string for the resolved code.
 *   4. Use the body's `request_id`; otherwise echo `X-Request-ID`.
 *   5. A missing `Response` (e.g. `fetch` rejected) yields
 *      `network_error` with `status === 0`.
 */
export function toApiError(body: unknown, response: Response | undefined): ApiError {
  const status = response?.status ?? 0;
  const headerRequestId = response?.headers.get("X-Request-ID") ?? undefined;

  const envelope = readEnvelope(body);
  const code: ApiErrorCode = envelope?.code ?? statusToCode(status);
  const message = envelope?.message?.trim() || FALLBACK_MESSAGES[code] || "Request failed.";
  // request_id may be present but non-string after a permissive parse;
  // only treat string values as candidates and otherwise defer to the
  // response header.
  const bodyRequestId = typeof envelope?.request_id === "string" ? envelope.request_id.trim() : undefined;
  const requestId = bodyRequestId || headerRequestId;

  return new ApiError({
    code,
    message,
    status,
    details: envelope?.details,
    requestId,
  });
}

// --- Classification helpers ------------------------------------------------
//
// Free functions that take any value (an `ApiError`, a raw `ApiErrorEnvelope`,
// or an unknown caught error) so UI code can route on a single helper
// regardless of where the error came from.

function codeOf(value: unknown): ApiErrorCode | undefined {
  if (value instanceof ApiError) return value.code;
  if (value && typeof value === "object") {
    const candidate = (value as Record<string, unknown>).code;
    if (typeof candidate === "string") return candidate as ApiErrorCode;
  }
  return undefined;
}

function statusOf(value: unknown): number | undefined {
  if (value instanceof ApiError) return value.status;
  return undefined;
}

function hasCode(value: unknown, code: ApiErrorCode): boolean {
  return codeOf(value) === code;
}

function hasAnyCode(value: unknown, codes: readonly ApiErrorCode[]): boolean {
  const c = codeOf(value);
  return c !== undefined && codes.includes(c);
}

export function isValidation(value: unknown): boolean {
  return hasCode(value, "validation_failed") || statusOf(value) === 400;
}

export function isAuth(value: unknown): boolean {
  return (
    hasCode(value, "unauthorized") ||
    hasCode(value, "forbidden") ||
    statusOf(value) === 401 ||
    statusOf(value) === 403
  );
}

export function isForbidden(value: unknown): boolean {
  return hasCode(value, "forbidden") || statusOf(value) === 403;
}

export function isNotFound(value: unknown): boolean {
  return hasCode(value, "not_found") || statusOf(value) === 404;
}

export function isConflict(value: unknown): boolean {
  return hasAnyCode(value, ["conflict", "idempotency_conflict"]) || statusOf(value) === 409;
}

export function isRateLimited(value: unknown): boolean {
  // quota_exceeded also comes back as 429, but it's a limit, not throttling.
  return hasCode(value, "rate_limited") || (statusOf(value) === 429 && !hasCode(value, "quota_exceeded"));
}

export function isQuotaExceeded(value: unknown): boolean {
  return hasCode(value, "quota_exceeded");
}

export function isReadiness(value: unknown): boolean {
  return hasCode(value, "readiness_check_failed") || statusOf(value) === 503;
}
